import {
  BillableIcon,
  BusinessUnitSelect,
  Button,
  Buttons,
  CancelButton,
  Checkbox,
  Currency,
  DisciplineSelect,
  LocationSelect,
  ModalCard,
  PracticeSelect,
  SearchInput,
  Table,
  Tooltip,
} from '~/components';
import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '~/styles';
import { Card } from '~/components/ModalCard';
import FixedDropdown from '~/components/FixedDropdown';
import { useFeatures } from '~/hooks';
import Fuse from 'fuse.js';

const StyledTable = styled(Table)`
  font-size: 0.875rem;
  margin-top: 1rem;

  opacity: ${({ fade }) => (fade ? 0.1 : 1)};

  height: 25rem;
  overflow-y: auto;

  ${Table.Cell} {
    align-items: flex-start;
    height: 100%;
    min-height: unset;
    padding: 1rem;
  }
`;

const StyledCheckbox = styled.div`
  > label > div {
    background: ${colors.white};
    width: 1.125rem;
    height: 1.125rem;
    font-size: 0.5rem;
    margin: 0;
  }
`;

const StyledModal = styled(ModalCard)`
  ${Card} {
    width: 72rem;
  }
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const StyledHeader = styled(Table.Header)`
  ${({ sticky }) =>
    sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 1;
    `}
`;

function isSameRole(a, b, features) {
  const sameName = a.name === b.name;
  const samePractice = a.practice?.id === b.practice?.id;
  const sameBusinessUnit = !features.businessUnits || a.businessUnit?.id === b.businessUnit?.id;
  const sameLocation = a.location?.id === b.location?.id;
  const sameCurrency = !a.isBillable || !b.isBillable || a.currency === b.currency;

  return sameName && samePractice && sameBusinessUnit && sameLocation && sameCurrency;
}

function getDuplicateTooltip(role, roles, action, features) {
  let tooltip = 'This role already exists for this client.';

  const duplicated = roles.some((clientRole) => {
    if (action === 'link' && clientRole.workspaceRoleId === role.id) {
      tooltip = 'This role is already linked with the client.';

      return true;
    }

    if (isSameRole(role, clientRole, features)) {
      if (clientRole.workspaceRoleId && action === 'link') {
        tooltip = 'This role is already linked with the client.';
      }

      return true;
    }

    return false;
  });

  return duplicated ? tooltip : null;
}

function WorkspaceRolesModal({ workspaceRoles, onChange, onClose, action, roles }) {
  const [selectedRoles, setSelectedRoles] = useState([]);
  const features = useFeatures();

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ practice: null, businessUnit: null, location: null, discipline: null });

  const searchKeys = useMemo(
    () =>
      [
        'name',
        features.practices && 'practice.name',
        features.businessUnits && 'businessUnit.name',
        'location.name',
        features.disciplines && 'discipline.name',
      ].filter(Boolean),
    [features.businessUnits, features.disciplines, features.practices],
  );

  const options = useMemo(() => {
    let result = workspaceRoles.filter((role) => {
      if (features.practices && filters.practice && role.practice?.id !== filters.practice.id) {
        return false;
      }

      if (features.businessUnits && filters.businessUnit && role.businessUnit?.id !== filters.businessUnit.id) {
        return false;
      }

      if (filters.location && role.location?.id !== filters.location.id) {
        return false;
      }

      if (features.disciplines && filters.discipline && role.discipline?.id !== filters.discipline.id) {
        return false;
      }

      return true;
    });

    if (!searchQuery) return result;

    const fuse = new Fuse(result, { keys: searchKeys, threshold: 0.4 });
    const results = fuse.search(searchQuery);

    return results.map((result) => result.item);
  }, [
    features.businessUnits,
    features.disciplines,
    features.practices,
    filters.businessUnit,
    filters.discipline,
    filters.location,
    filters.practice,
    searchKeys,
    searchQuery,
    workspaceRoles,
  ]);

  const selectableOptions = useMemo(
    () => options.filter((role) => !getDuplicateTooltip(role, roles, action, features)),
    [action, features, options, roles],
  );

  const selected = useMemo(() => {
    const selectableRoleIds = new Set(selectableOptions.map(({ id }) => id));
    const visibleSelectedRoles = selectedRoles.filter(({ id }) => selectableRoleIds.has(id));
    const sourceRoleIds = new Set(workspaceRoles.map(({ id }) => id));

    return {
      get any() {
        return selectedRoles.some(({ id }) => sourceRoleIds.has(id));
      },
      get visibleSome() {
        return visibleSelectedRoles.length > 0;
      },
      get visibleAll() {
        return selectableOptions.length > 0 && visibleSelectedRoles.length === selectableOptions.length;
      },
    };
  }, [selectableOptions, selectedRoles, workspaceRoles]);

  const handleBulkSelectionChange = () => {
    const selectableRoleIds = new Set(selectableOptions.map(({ id }) => id));

    setSelectedRoles((currentSelectedRoles) => {
      if (selected.visibleAll) {
        return currentSelectedRoles.filter(({ id }) => !selectableRoleIds.has(id));
      }

      const selectedRoleIds = new Set(currentSelectedRoles.map(({ id }) => id));

      return [...currentSelectedRoles, ...selectableOptions.filter(({ id }) => !selectedRoleIds.has(id))];
    });
  };

  const handleAdd = () => {
    onChange(selectedRoles);
    onClose();
  };

  const handleSearchChange = ({ target: { value } }) => {
    setSearchQuery(value);
  };

  const handleFilterChange = ({ target: { name, value } }) => {
    setFilters((currentFilters) => ({ ...currentFilters, [name]: value }));
  };

  return (
    <StyledModal title={`${{ add: 'Add', link: 'Link' }[action]} Workspace Roles`} onClose={onClose}>
      <ModalCard.Body>
        <SearchInput
          value={searchQuery}
          placeholder="All"
          materialPlaceholder="Search"
          materialAlwaysVisible
          onChange={handleSearchChange}
        />
        <Filters>
          {features.practices && (
            <PracticeSelect
              name="practice"
              value={filters.practice}
              placeholder="Practice"
              onChange={handleFilterChange}
              component={FixedDropdown}
            />
          )}
          {features.businessUnits && (
            <BusinessUnitSelect
              name="businessUnit"
              value={filters.businessUnit}
              placeholder="Business Unit"
              onChange={handleFilterChange}
              component={FixedDropdown}
            />
          )}
          <LocationSelect
            name="location"
            value={filters.location}
            placeholder="Location"
            onChange={handleFilterChange}
            component={FixedDropdown}
          />
          {features.disciplines && (
            <DisciplineSelect
              name="discipline"
              value={filters.discipline}
              placeholder="Discipline"
              onChange={handleFilterChange}
              component={FixedDropdown}
            />
          )}
        </Filters>
        <StyledTable>
          <StyledHeader sticky>
            <Table.Column align="center" width="2.5rem">
              <StyledCheckbox>
                <Checkbox
                  checked={selected.visibleSome || false}
                  partial={selected.visibleSome && !selected.visibleAll}
                  onChange={handleBulkSelectionChange}
                />
              </StyledCheckbox>
            </Table.Column>
            <Table.Column width="15rem">Role Name</Table.Column>
            <Table.Column isVisible={features.practices} width="10rem">
              Practice
            </Table.Column>
            <Table.Column isVisible={features.businessUnits} width="10rem">
              Business Unit
            </Table.Column>
            <Table.Column width="10rem">Location</Table.Column>
            <Table.Column isVisible={features.disciplines} width="10rem">
              Discipline
            </Table.Column>
            <Table.Column align="right">Rate</Table.Column>
          </StyledHeader>
          <Table.Body>
            {options.map((role) => {
              const checked = !!selectedRoles.find((r) => r.id === role.id);
              const tooltip = getDuplicateTooltip(role, roles, action, features);
              const duplicated = !!tooltip;

              const handleSelectionChange = () => {
                setSelectedRoles((currentSelectedRoles) =>
                  checked ? currentSelectedRoles.filter(({ id }) => id !== role.id) : [...currentSelectedRoles, role],
                );
              };

              return (
                <Table.Row key={role.id}>
                  <Table.Cell>
                    <Tooltip message={duplicated ? tooltip : undefined}>
                      <StyledCheckbox>
                        <Checkbox disabled={duplicated} checked={checked} onChange={handleSelectionChange} />
                      </StyledCheckbox>
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell>{role.name}</Table.Cell>
                  <Table.Cell>{role.practice?.name}</Table.Cell>
                  <Table.Cell>{role.businessUnit?.name}</Table.Cell>
                  <Table.Cell>{role.location?.name}</Table.Cell>
                  <Table.Cell>{role.discipline?.name}</Table.Cell>
                  <Table.Cell>
                    <Currency
                      value={role.isBillable ? role.rate : null}
                      maximumFractionDigits={7}
                      currency={role.currency}
                    />
                    <BillableIcon style={{ marginLeft: '0.4rem' }} value={role.isBillable} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </StyledTable>
      </ModalCard.Body>
      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={onClose}>Close</CancelButton>

          <Button disabled={!selected.any} onClick={handleAdd}>
            {{ add: 'Add', link: 'Link' }[action]}
          </Button>
        </Buttons>
      </ModalCard.Footer>
    </StyledModal>
  );
}

export default WorkspaceRolesModal;
